import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, UserPlus, Package, Building2, User, FileText, MapPin, AlertTriangle } from 'lucide-react';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { api } from '@/shared/api/axios';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { CURRENCY_OPTIONS } from '@/features/deals/constants';
import {
  FollowupCaptureFields,
  followupCapturePayload,
  EMPTY_FOLLOWUP_CAPTURE,
  type FollowupCapture,
} from '@/features/activity/components/FollowupCaptureFields';
import { formatINR } from '@/shared/utils/currency';
import { useSalesAssigneeOptions } from '@/shared/hooks/useSalesAssignees';
import { useUser } from '@/shared/hooks/useAuth';

import { usePipelineStages, usePipelineMutations } from '../hooks/usePipeline';
import { ProductLinesEditor, calculateAllLinesTotals, resplitLinesForSupplyType } from '../components/ProductLinesEditor';
import { useBillToPreview, type BillToAddress } from '@/features/quotations/hooks/useQuotationForm';
import { CustomerPoFields, EMPTY_CUSTOMER_PO, type CustomerPoValue } from '../components/CustomerPoFields';
import { ContactSelectField, type ContactSelection } from '../components/ContactSelectField';
import { staffIdentityProblem } from '@/shared/utils/staffIdentity';
import { Modal } from '@/shared/components/ui/Modal';
import { ContactFormPage } from '@/features/contacts/pages/ContactFormPage';
import type { PipelineLine } from '../types';

/**
 * Starting stages offered for hand-entered enquiries / walk-ins.
 */
const START_STAGES = ['new_lead', 'contacted', 'qualified', 'requirement_received', 'quotation_sent'];

export function NewPipelineRecordPage() {
  const navigate = useNavigate();
  const { data: stageData } = usePipelineStages();
  const { create } = usePipelineMutations();
  const currentUser = useUser();

  // A rep entering their own enquiry owns it. Shown in the field rather than
  // applied invisibly at save time, and still changeable before submitting.
  // Only for Sales: an admin typing up a walk-in is not the person who will
  // work it, so their records stay unassigned for someone to allocate.
  useEffect(() => {
    if (String(currentUser?.department || '').toLowerCase() === 'sales' && currentUser?.id) {
      setForm((f) => (f.assigned_to ? f : { ...f, assigned_to: currentUser.id }));
    }
  }, [currentUser]);

  const [form, setForm] = useState({
    contactId: '',
    business_name: '',
    contact_name: '',
    phone: '',
    email: '',
    clientType: '',
    source: 'direct',
    stage: 'contacted',
    // Pre-selected below for a sales user, so who owns the enquiry is visible
    // before saving rather than being decided silently on the server.
    assigned_to: '',
    value: '',
    currency: 'INR',
    notes: '',
    lead_disposition: '',
    disposition_reason: '',
    disposition_notes: '',
  });

  // Item 44 — the canonical follow-up capture set, shared with the Follow-up
  // panel, the quotation form and the quick action. Checklist item 43 keeps the
  // date mandatory; it deliberately starts empty rather than pre-filled with
  // "tomorrow", because a defaulted date is a decision nobody made.
  const [followup, setFollowup] = useState<FollowupCapture>(EMPTY_FOLLOWUP_CAPTURE);

  const [billingAddress, setBillingAddress] = useState<BillToAddress>({});
  const [shippingAddress, setShippingAddress] = useState<BillToAddress>({});
  // A new client begins blank; an existing client starts with the Ship-to
  // saved in Client Profile. The rep may overwrite it for a one-off site.
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [saveShippingAsDefault, setSaveShippingAsDefault] = useState(true);

  const [lines, setLines] = useState<PipelineLine[]>([]);
  const [lineDiscountType, setLineDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [po, setPo] = useState<CustomerPoValue>({ ...EMPTY_CUSTOMER_PO });
  const [manualValueChanged, setManualValueChanged] = useState(false);

  // The GST policy lives on the server (see useBillToPreview) — same call the
  // Quotation form makes. Defaults to intra-state, same as Quotations, until
  // a contact resolves otherwise.
  const { data: billToPreview } = useBillToPreview(form.contactId || null);
  const intraState = billToPreview?.intraState ?? true;

  // Sync address from resolved contact
  useEffect(() => {
    if (billToPreview?.billTo) {
      const b = billToPreview.billTo.billingAddress || {};
      const s = billToPreview.billTo.shippingAddress || {};
      setBillingAddress(b);
      const hasSavedShipTo = Boolean(s.companyName || s.name || s.street || s.city || s.state || s.zipCode || s.phone || s.email);
      setShippingAddress(hasSavedShipTo ? s : {});
      setSameAsBilling(false);
    }
  }, [billToPreview]);

  // Re-split every line already on the record when the resolved supply type
  // changes — e.g. a contact is picked (or swapped for one in another state)
  // AFTER lines were already added from the default intra-state assumption.
  // Without this, a line keeps whatever split it was given at add-time even
  // once the correct one is known to be different.
  const [lastIntraState, setLastIntraState] = useState(intraState);
  useEffect(() => {
    if (intraState === lastIntraState) return;
    setLastIntraState(intraState);
    setLines((prev) => (prev.length ? resplitLinesForSupplyType(prev, intraState) : prev));
  }, [intraState, lastIntraState]);

  // A second way into "create a new client" besides ContactSelectField's own
  // dropdown — typing straight into Company / Business Name with no account
  // chosen is the natural first move for a brand-new client, and the only
  // feedback it got before was a rejection toast at submit time with no next
  // step in view. This surfaces the fix right where the confusion happens.
  const [createContactOpen, setCreateContactOpen] = useState(false);
  // Set when the server flags a probable double entry for this client — a
  // proper dialog instead of the browser's own window.confirm(), which reads
  // as a raw "localhost:5175 says" popup with none of the app's styling.
  const [duplicateWarning, setDuplicateWarning] = useState<{ id: string; name: string; stageLabel: string }[] | null>(null);

  const [duplicateInfo, setDuplicateInfo] = useState<{
    on: string;
    type: string;
    match: { _id: string; name: string; company: string; phone: string; gstin: string; ownerName: string; email?: string };
    message: string;
  } | null>(null);

  const debouncedPhone = useDebounce(form.phone.trim(), 300);
  const debouncedGstin = useDebounce((billingAddress.gstin || '').trim(), 300);

  useEffect(() => {
    const cleanPhone = debouncedPhone.replace(/\D/g, '');
    const cleanGstin = debouncedGstin.trim().toUpperCase();
    if (cleanPhone.length < 10 && cleanGstin.length < 15) {
      setDuplicateInfo(null);
      return;
    }

    let active = true;
    api.post('/api/contacts-identity/check-duplicate', {
      phone: cleanPhone.length >= 10 ? cleanPhone : undefined,
      gstin: cleanGstin.length >= 15 ? cleanGstin : undefined,
    }).then(({ data }) => {
      if (active) {
        if (data?.duplicate && data?.match) {
          if (form.contactId && String(form.contactId) === String(data.match._id)) {
            setDuplicateInfo(null);
          } else {
            setDuplicateInfo(data);
          }
        } else {
          setDuplicateInfo(null);
        }
      }
    }).catch(() => {
      if (active) setDuplicateInfo(null);
    });

    return () => { active = false; };
  }, [debouncedPhone, debouncedGstin, form.contactId]);
  // The SAME full Contacts form the dropdown opens — not the cut-down modal.
  // Two entry points, one form: a client created from either has its GSTIN and
  // address, which is exactly what the quotation raised minutes later needs.
  const handleContactCreated = (c: { id: string; name: string; company: string; phone: string; email: string }) => {
    applyContact({
      contactId: c.id,
      business_name: c.company || c.name || form.business_name,
      contact_name: c.company && c.name !== c.company ? c.name : '',
      phone: c.phone,
      email: c.email,
    });
    setCreateContactOpen(false);
    toast.success('Client account created and linked.');
  };

  // Sync expected value with product lines grand total if user hasn't overridden it manually
  useEffect(() => {
    if (lines.length > 0 && !manualValueChanged) {
      const totals = calculateAllLinesTotals(lines);
      if (totals.grandTotal > 0) {
        setForm((f) => ({ ...f, value: String(totals.grandTotal) }));
      }
    }
  }, [lines, manualValueChanged]);

  const applyContact = (sel: ContactSelection) =>
    setForm((f) => ({
      ...f,
      contactId: sel.contactId,
      business_name: sel.business_name || f.business_name,
      // A different client brings its OWN person/phone/email — even blank ones.
      // Keeping the previous pick's values here is how one client's (or a
      // BDM's) details ended up on another client's record.
      ...(sel.contactId && sel.contactId !== f.contactId
        ? { contact_name: sel.contact_name || '', phone: sel.phone || '', email: sel.email || '' }
        : { contact_name: sel.contact_name || f.contact_name, phone: sel.phone || f.phone, email: sel.email || f.email }),
    }));

  const set = (k: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    if (k === 'value') {
      setManualValueChanged(true);
    }
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const stageOptions = (stageData?.stages ?? [])
    .filter((s) => START_STAGES.includes(s.key))
    .map((s) => ({ value: s.key, label: s.label }));

  // Sales only — a pipeline record assigned outside Sales is invisible to
  // whoever it was given to. See useSalesAssignees.
  const userOptions = useSalesAssigneeOptions(form.assigned_to);

  // Stashed here (not state) so the duplicate-warning dialog's "Create
  // anyway" button — rendered outside submit()'s own scope — can retry the
  // exact same payload with confirmDuplicate: true, without re-deriving it.
  const pendingPayloadRef = useRef<Record<string, unknown> | null>(null);

  const send = (confirmDuplicate: boolean) => {
    const payload = pendingPayloadRef.current;
    if (!payload) return;
    create.mutate(
      confirmDuplicate ? { ...payload, confirmDuplicate: true } : payload,
      {
        onSuccess: (r) => {
          toast.success('Pipeline record created successfully.');
          // `replace: true` keeps the completed form out of browser history;
          // Back returns to the pipeline list rather than inviting an
          // accidental re-submit of this same opportunity.
          navigate(`/pipeline/${r.id}`, { replace: true });
        },
        onError: (err) => {
          // The server flags a probable double entry (an open record for this
          // client created in the last 24 hours) rather than refusing it — repeat
          // business is normal, so the rep decides.
          const dupes = duplicateRecords(err);
          if (dupes && !confirmDuplicate) {
            setDuplicateWarning(dupes);
            return;
          }
          // Item 62 — duplicate GST is refused outright. The "create anyway"
          // button above is for an ordinary repeat enquiry, and it used to send
          // the flag that ALSO waived this rule.
          const gstClash = gstDuplicateRecord(err);
          if (gstClash) {
            toast.error(gstClash);
            return;
          }
          toast.error(errText(err, 'Could not create the record.'));
        },
      },
    );
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const businessName = form.business_name.trim();
    const staffProblem = staffIdentityProblem({ name: form.contact_name, email: form.email });
    if (staffProblem) {
      toast.error(staffProblem);
      return;
    }
    if (!form.contactId) {
      toast.error('Choose the client account this enquiry belongs to.');
      return;
    }
    if (!businessName) {
      toast.error('A company / business name is required.');
      return;
    }
    if (!form.clientType) {
      toast.error('Please select Client Type (New Client or Old Client).');
      return;
    }
    if (!lines.length || lines.every((l) => !l.productId && !l.productName && !l.description)) {
      toast.error('Please add at least one product line item to save the pipeline.');
      return;
    }
    if (!followup.followupDate) {
      toast.error('Set a follow-up date before saving — this is the date the enquiry comes back to you.');
      return;
    }

    const payload = {
        // `name` stays the record's display identity everywhere else in the app
        // (Kanban cards, exports, activity messages), so the company is written
        // to both rather than teaching every one of those readers a new field.
        name: businessName,
        business_name: businessName,
        contactId: form.contactId,
        contact_name: form.contact_name.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        clientType: form.clientType,
        source: form.source,
        stage: form.stage,
        lead_disposition: form.lead_disposition || undefined,
        disposition_reason: form.disposition_reason || undefined,
        disposition_notes: form.disposition_notes.trim() || undefined,
        assigned_to: form.assigned_to || undefined,
        value: form.value ? Number(form.value) : undefined,
        currency: form.currency,
        notes: form.notes.trim() || undefined,
        // Creates the first follow-up on the server (src/api-pipeline/index.js),
        // which refuses the record if the date is missing or unparseable.
        followup: followupCapturePayload(followup),
        billingAddress,
        shippingAddress: sameAsBilling ? billingAddress : shippingAddress,
        // A distinct Ship-to can be retained on the client account, so the
        // next pipeline record and every document start with it filled in.
        saveShippingAsDefault: !sameAsBilling && saveShippingAsDefault,
        lines: lines.length ? lines : undefined,
        ...po,
        customerPoDate: po.customerPoDate || undefined,
    };

    pendingPayloadRef.current = payload;
    send(false);
  };

  return (
    <div className="w-full flex-1 flex flex-col gap-6 p-4 md:p-6 lg:p-8">
      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-outline/60 pb-5">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/pipeline')}
            aria-label="Back to pipeline"
            className="rounded-xl h-10 w-10 border-outline bg-surface"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-body-xs font-semibold uppercase tracking-wider text-primary">Sales Pipeline</span>
              <span className="text-body-xs text-on-surface-variant">/</span>
              <span className="text-body-xs text-on-surface-variant">New Entry</span>
            </div>
            <h1 className="flex items-center gap-2.5 text-2xl font-bold text-on-surface mt-0.5">
              <UserPlus className="h-6 w-6 text-primary" />
              New Pipeline Record
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate('/pipeline')}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="pipeline-record-form"
            disabled={create.isPending}
            className="px-5 shadow-sm"
          >
            {create.isPending ? 'Creating Record…' : 'Create Record'}
          </Button>
        </div>
      </header>

      {/* ── Full Width Form ────────────────────────────────────────────────── */}
      <form id="pipeline-record-form" onSubmit={submit} className="flex flex-col gap-6 w-full">
        {/* Top Grid: Contact Identity & Commercial Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
          {/* Card 1: Contact Information */}
          <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <User className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Client & Contact</h2>
            </div>

            <div className="flex flex-col gap-3.5">
              {/* The client account is CHOSEN, never guessed — see
                  ContactSelectField for what auto-matching cost us. */}
              <label className="flex flex-col gap-1.5">
                <span className="text-body-sm font-medium text-on-surface">Client Account *</span>
                <ContactSelectField
                  contactId={form.contactId}
                  onSelect={applyContact}
                  onClear={() => setForm((f) => ({ ...f, contactId: '' }))}
                />
              </label>

              {duplicateInfo && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 text-body-xs text-amber-950 shadow-xs flex flex-col gap-2 animate-in fade-in-50 duration-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-amber-950">
                        ⚠️ Already registered with {duplicateInfo.match.company || duplicateInfo.match.name}, Owner: {duplicateInfo.match.ownerName}
                      </p>
                      <p className="text-amber-800 text-caption mt-0.5">
                        Matched on {duplicateInfo.on.toUpperCase()} ({duplicateInfo.on === 'phone' ? duplicateInfo.match.phone : duplicateInfo.match.gstin}).
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="self-start text-caption h-7 px-2.5 bg-white border-amber-400 hover:bg-amber-100 text-amber-900"
                    onClick={() => {
                      applyContact({
                        contactId: duplicateInfo.match._id,
                        business_name: duplicateInfo.match.company || duplicateInfo.match.name,
                        contact_name: duplicateInfo.match.name,
                        phone: duplicateInfo.match.phone,
                        email: duplicateInfo.match.email || '',
                      });
                      setDuplicateInfo(null);
                    }}
                  >
                    Use this existing client account
                  </Button>
                </div>
              )}

              {/* Filled from the chosen account and editable: the person and
                  number on THIS enquiry may be a site contact rather than the
                  account's main one. Editing here does not change the account. */}
              <Input
                label="Company / Business Name *"
                value={form.business_name}
                onChange={set('business_name')}
                placeholder="Choose a client account above"
                required
              />

              {/* Checklist item 43 — the follow-up is what keeps an enquiry
                  alive, and until now nothing asked for it, so the follow-up
                  centre filled only with the records someone remembered to
                  schedule. Deliberately not defaulted to a date: a pre-filled
                  tomorrow is a decision nobody made. */}
              <FollowupCaptureFields
                value={followup}
                onChange={setFollowup}
                dateLabel="Follow-up Date *"
              />

              {/* The single most common point of confusion here: typing a
                  company name directly into the field above feels like enough,
                  but Save still refuses without a chosen Client Account — this
                  is that field's own escape hatch, not just the one inside the
                  search dropdown. */}
              {!form.contactId && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-body-xs text-amber-900">
                  {form.business_name.trim() ? (
                    <>No client account is linked yet for <strong>{form.business_name.trim()}</strong>. </>
                  ) : (
                    <>A client account is required before this record can be saved. </>
                  )}
                  <button
                    type="button"
                    onClick={() => setCreateContactOpen(true)}
                    className="font-bold underline underline-offset-2 hover:no-underline"
                  >
                    {form.business_name.trim()
                      ? `Create a client account for "${form.business_name.trim()}"`
                      : 'Create a new client account'}
                  </button>
                </div>
              )}

              <Input
                label="Contact Person"
                value={form.contact_name}
                onChange={set('contact_name')}
                placeholder="e.g. Rajesh Kumar"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Phone Number"
                  value={form.phone}
                  onChange={set('phone')}
                  inputMode="tel"
                  placeholder="+91 98765 43210"
                />
                <Input
                  label="Email Address"
                  value={form.email}
                  onChange={set('email')}
                  type="email"
                  placeholder="contact@company.com"
                />
              </div>
            </div>
          </div>

          {/* Card 2: Pipeline Classification & Commercial */}
          <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <Building2 className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Pipeline & Status</h2>
            </div>

            <div className="flex flex-col gap-3.5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Select
                  label="Client Type *"
                  value={form.clientType}
                  onChange={set('clientType')}
                  required
                  options={[
                    { value: '', label: 'Select Client Type...' },
                    { value: 'New Client', label: 'New Client' },
                    { value: 'Old Client', label: 'Old Client' },
                  ]}
                />

                <Select
                  label="Lead Source"
                  value={form.source}
                  onChange={set('source')}
                  options={[
                    { value: 'direct', label: 'Direct / Walk-in' },
                    { value: 'referral', label: 'Referral' },
                    { value: 'cold_call', label: 'Cold Call' },
                    { value: 'exhibition', label: 'Exhibition / Trade Fair' },
                    { value: 'indiamart', label: 'IndiaMART' },
                    { value: 'website', label: 'Website / Inbound' },
                    { value: 'other', label: 'Other' },
                  ]}
                />

                <Select
                  label="Starting Stage"
                  value={form.stage}
                  onChange={set('stage')}
                  options={stageOptions.length ? stageOptions : [{ value: 'contacted', label: 'Contacted' }]}
                />
              </div>

              <Select
                label="Assigned Employee / BDM"
                value={form.assigned_to}
                onChange={set('assigned_to')}
                options={userOptions}
              />

              <div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
                  {/* Carried into the quotation raised from this record, which
                      opens billed in this currency. */}
                  <Select
                    label="Currency"
                    value={form.currency}
                    onChange={set('currency')}
                    options={CURRENCY_OPTIONS}
                  />
                  <Input
                    label={`Expected Value (${form.currency === 'INR' ? '₹' : form.currency})`}
                    value={form.value}
                    onChange={set('value')}
                    inputMode="numeric"
                    placeholder="Estimated revenue..."
                  />
                </div>
                {lines.length > 0 && (
                  <p className="mt-1 text-body-xs text-on-surface-variant">
                    💡 Auto-calculated from product lines ({formatINR(Number(form.value) || 0)}). You can also override it.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Card 3: Notes & Requirements Summary */}
          <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <FileText className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Enquiry Notes & Context</h2>
            </div>

            <div className="flex flex-col flex-1 gap-2">
              <label className="flex flex-col flex-1 gap-1.5">
                <span className="text-body-sm font-medium text-on-surface">Client Requirement / Notes</span>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  rows={6}
                  className="flex-1 w-full rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition"
                  placeholder="Specify client context, required industrial lubricants, current issues, target dispatch dates, or custom terms..."
                />
              </label>
            </div>
          </div>
        </div>

        {/* Section 2: Products & Requirements Line Items (Full Width) */}
        <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline/60 pb-4">
            <div>
              <h2 className="flex items-center gap-2 text-body-base font-bold text-on-surface">
                <Package className="h-5 w-5 text-primary" />
                Product Line Items & Pricing (Mandatory) <span className="text-red-500">*</span>
              </h2>
              <p className="text-body-xs text-on-surface-variant mt-0.5">
                At least one product line item is required to save the sales pipeline. Select items from catalog to auto-fill MRP, base rate, unit and capacity.
              </p>
            </div>
          </div>

          <ProductLinesEditor lines={lines} onChange={setLines} intraState={intraState} currency={form.currency || 'INR'} discountType={lineDiscountType} onDiscountTypeChange={(type) => { setLineDiscountType(type); setLines((previous) => previous.map((line) => ({ ...line, discountType: type }))); }} />
        </div>

        {/* Section: Shipping / Delivery Address */}
        <div className="rounded-2xl border border-outline bg-surface p-5 shadow-sm flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline/60 pb-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Delivery Address (Ship To)</h2>
            </div>
            <label className="flex items-center gap-2 text-body-sm text-on-surface-variant cursor-pointer">
              <input
                type="checkbox"
                checked={sameAsBilling}
                onChange={(e) => {
                  const next = e.target.checked;
                  setSameAsBilling(next);
                  if (!next) setShippingAddress({});
                }}
                className="rounded border-outline text-primary focus:ring-primary"
              />
              Same as billing address
            </label>
          </div>

          {sameAsBilling ? (
            <p className="text-body-sm text-on-surface-variant py-1">
              {[
                billingAddress.street,
                [billingAddress.city, billingAddress.zipCode].filter(Boolean).join(' '),
                billingAddress.state,
                billingAddress.country,
              ].filter(Boolean).join(', ') || 'Using client account billing address (auto-populated once a client is chosen).'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Input
                  label="Company / Consignee Name (Ship To)"
                  value={(shippingAddress as any).companyName || (shippingAddress as any).name || ''}
                  onChange={(e) => setShippingAddress((prev: any) => ({ ...prev, companyName: e.target.value, name: e.target.value }))}
                  placeholder="e.g. Warehouse / Delivery Company Name"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <span className="text-body-xs font-medium text-on-surface">Street / Delivery Address</span>
                <textarea
                  value={shippingAddress.street || ''}
                  onChange={(e) => setShippingAddress((prev) => ({ ...prev, street: e.target.value }))}
                  rows={2}
                  placeholder="Plot / factory / warehouse delivery address..."
                  className="w-full rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
              </div>
              <Input
                label="City"
                value={shippingAddress.city || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, city: e.target.value }))}
                placeholder="e.g. Pune"
              />
              <Input
                label="State"
                value={shippingAddress.state || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, state: e.target.value }))}
                placeholder="e.g. Maharashtra"
              />
              <Input
                label="Pincode / Postal Code"
                value={shippingAddress.zipCode || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, zipCode: e.target.value }))}
                placeholder="e.g. 411013"
              />
              <Input
                label="Country"
                value={shippingAddress.country || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, country: e.target.value }))}
                placeholder="e.g. India"
              />
              <Input
                label="Mobile / Phone (Ship To)"
                value={(shippingAddress as any).phone || ''}
                onChange={(e) => setShippingAddress((prev: any) => ({ ...prev, phone: e.target.value }))}
                placeholder="e.g. +91 98765 43210 (optional)"
              />
              <Input
                label="Email (Ship To)"
                value={(shippingAddress as any).email || ''}
                onChange={(e) => setShippingAddress((prev: any) => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. delivery@company.com (optional)"
              />
            </div>
          )}
          {!sameAsBilling && (
            <label className="flex items-center gap-2 text-body-sm text-on-surface-variant cursor-pointer">
              <input
                type="checkbox"
                checked={saveShippingAsDefault}
                onChange={(e) => setSaveShippingAsDefault(e.target.checked)}
                className="rounded border-outline text-primary focus:ring-primary"
              />
              Save this as the client's default Ship To address
            </label>
          )}
        </div>

        {/* The PO usually arrives at the END of negotiation, so this is
            normally filled in later from the record's own page — but a
            walk-in who brings their PO with them can record it right here. */}
        <CustomerPoFields value={po} onChange={setPo} />

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-end gap-3 rounded-2xl border border-outline bg-surface p-4 shadow-sm">
          <Button type="button" variant="ghost" onClick={() => navigate('/pipeline')}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending} className="px-6">
            {create.isPending ? 'Creating Record…' : 'Create Pipeline Record'}
          </Button>
        </div>
      </form>

      <Modal
        open={createContactOpen}
        onClose={() => setCreateContactOpen(false)}
        title="New Client Account"
        size="2xl"
        // Several tabs' worth of input — a stray backdrop click must not lose it.
        closeOnBackdrop={false}
      >
        <ContactFormPage
          embedded
          onCreated={handleContactCreated}
          onCancel={() => setCreateContactOpen(false)}
          initialValues={{ company: form.business_name, name: form.business_name }}
        />
      </Modal>

      <Modal
        open={Boolean(duplicateWarning)}
        onClose={() => setDuplicateWarning(null)}
        title="This client already has an open enquiry"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDuplicateWarning(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setDuplicateWarning(null);
                send(true);
              }}
            >
              Create another one anyway
            </Button>
          </div>
        }
      >
        {duplicateWarning && (
          <div className="space-y-3">
            <p className="text-body-sm text-on-surface-variant">
              Created in the last 24 hours — repeat business is normal, but confirm this is a separate enquiry:
            </p>
            <ul className="space-y-1.5">
              {duplicateWarning.map((d) => (
                <li key={d.id} className="rounded-lg border border-outline bg-surface-container-low px-3 py-2 text-body-sm">
                  <span className="font-semibold text-on-surface">{d.name}</span>
                  <span className="text-on-surface-variant"> — {d.stageLabel}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
}

function duplicateRecords(err: unknown): { id: string; name: string; stageLabel: string }[] | null {
  const data = (err as { response?: { status?: number; data?: { code?: string; duplicates?: { id: string; name: string; stageLabel: string }[] } } })?.response;
  if (data?.status === 409 && data.data?.code === 'DUPLICATE_OPEN_RECORD' && data.data.duplicates?.length) return data.data.duplicates;
  return null;
}

/** Item 62: the server's refusal to duplicate a GSTIN, with the owner named. */
function gstDuplicateRecord(err: unknown): string | null {
  const data = (err as { response?: { status?: number; data?: { code?: string; error?: string } } })?.response;
  if (data?.status === 409 && data.data?.code === 'DUPLICATE_GST_RECORD') {
    return data.data.error || 'An active pipeline record already holds this GSTIN.';
  }
  return null;
}

function errText(err: unknown, fallback: string): string {  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}

export default NewPipelineRecordPage;
